import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import { ORGANIZATIONS_SEARCH_KEY } from 'api/search'
import axios from 'axios'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Organization } from 'types'

const createOrganization = async (orgData: Partial<Organization>): Promise<Organization> => {
  const url = `${baseUrl}/aws/organizations_table/organization`
  console.log('New Organization Data: ', orgData)
  const response = await axios.post(url, orgData)
  return response.data // Assumes the response data includes the new contact with `id`
}

export const usePostOrganization = (queryKey = ['organization']) => {
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const mutation = useMutation<Organization, Error, Partial<Organization>, { previousOrgs: Organization[] | undefined }>({
    mutationFn: createOrganization,
    onMutate: async (newOrg): Promise<{ previousOrgs: Organization[] | undefined }> => {
      await queryClient.cancelQueries({ queryKey: ['organization'] })

      // Snapshot previous organization
      const previousOrgs = queryClient.getQueryData<Organization[]>(['organization'])

      // Optimistically update cache
      queryClient.setQueryData<Organization[]>(['organization'], old =>
        old ? [...old, { ...newOrg, org_uuid: 'temp-id' } as Organization] : [{ ...newOrg, org_uuid: 'temp-id' } as Organization]
      )

      return { previousOrgs }
    },
    onError: (error, _newOrg, context) => {
      if (context?.previousOrgs) {
        queryClient.setQueryData(['organization'], context.previousOrgs)
      }
      console.error('[usePostOrganization] Mutation Error:', error)
      showSnackbar((error as any).response?.data?.message || error.message, 'error')
    },
    onSuccess: data => {
      console.log('[usePostOrganization] Org Created Successfully:', data)

      // Update the cache with the actual org UUID
      queryClient.setQueryData<Organization[]>(['organization'], oldOrgs =>
        oldOrgs ? oldOrgs.map(org => (org.org_uuid === 'temp-id' ? { ...data } : org)) : [data]
      )

      showSnackbar('Org created successfully', 'success')
    },
    onSettled: (data, error) => {
      console.log('data: ', data, 'error: ', error)
      queryClient.invalidateQueries({ queryKey: queryKey })
      // Invalidate search cache when organization is created
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_SEARCH_KEY })
    },
  })

  return mutation
}
